/**
 * Content translation maps for dynamic lead data (EN → ZH).
 * Used when the user selects Chinese language — translates values
 * that come from the database/API in English.
 */
import type { Lang } from './LanguageContext';

// ── Entity status ─────────────────────────────────────────────────────
const STATUS_ZH: Record<string, string> = {
  active: '活跃',
  dissolved: '已注销',
  unknown: '未知',
};

// ── Risk tiers ────────────────────────────────────────────────────────
const RISK_TIER_ZH: Record<string, string> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '严重',
};

// ── Payment behavior ──────────────────────────────────────────────────
const PAYMENT_ZH: Record<string, string> = {
  Prompt: '及时',
  'Generally Prompt': '一般及时',
  'Occasionally Slow': '偶尔延迟',
  Slow: '缓慢',
  'No History': '无记录',
  Unknown: '未知',
};

// ── Credit rating ─────────────────────────────────────────────────────
const CREDIT_RATING_ZH: Record<string, string> = {
  A: 'A级',
  B: 'B级',
  C: 'C级',
  D: 'D级',
};

// ── Industry relevance ────────────────────────────────────────────────
const RELEVANCE_ZH: Record<string, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

// ── Geographic markets / regions ──────────────────────────────────────
const MARKET_ZH: Record<string, string> = {
  'North America': '北美',
  'Latin America': '拉丁美洲',
  'South America': '南美',
  'Central America': '中美洲',
  Europe: '欧洲',
  'Central Europe': '中欧',
  'Eastern Europe': '东欧',
  'Southern Europe': '南欧',
  'Asia Pacific': '亚太',
  'Southeast Asia': '东南亚',
  'South Asia': '南亚',
  'Middle East': '中东',
  Africa: '非洲',
  'North Africa': '北非',
  'East Africa': '东非',
  'West Africa': '西非',
  'Southern Africa': '南非地区',
  Oceania: '大洋洲',
  Scandinavia: '斯堪的纳维亚',
  'Gulf States': '海湾国家',
  'Andean Region': '安第斯地区',
  'Southern Cone': '南锥体',
  'Iberian Peninsula': '伊比利亚半岛',
  Balkans: '巴尔干',
  Baltics: '波罗的海',
  Caribbean: '加勒比',
  CIS: '独联体',
  Global: '全球',
  Japan: '日本',
  'South Korea': '韩国',
  India: '印度',
  Russia: '俄罗斯',
  'Russia, CIS': '俄罗斯、独联体',
  Vietnam: '越南',
  Thailand: '泰国',
  Malaysia: '马来西亚',
  Indonesia: '印度尼西亚',
  Pakistan: '巴基斯坦',
  'Sri Lanka': '斯里兰卡',
  Moldova: '摩尔多瓦',
  Bolivia: '玻利维亚',
  Algeria: '阿尔及利亚',
  Ecuador: '厄瓜多尔',
  Belarus: '白俄罗斯',
  Ukraine: '乌克兰',
};

// ── Country names ─────────────────────────────────────────────────────
const COUNTRY_ZH: Record<string, string> = {
  'United States': '美国',
  'United Kingdom': '英国',
  France: '法国',
  Germany: '德国',
  Japan: '日本',
  'South Korea': '韩国',
  China: '中国',
  'Hong Kong': '香港',
  Taiwan: '台湾',
  Australia: '澳大利亚',
  'New Zealand': '新西兰',
  Canada: '加拿大',
  Mexico: '墨西哥',
  Brazil: '巴西',
  India: '印度',
  Singapore: '新加坡',
  Malaysia: '马来西亚',
  Thailand: '泰国',
  Vietnam: '越南',
  Indonesia: '印度尼西亚',
  Philippines: '菲律宾',
  'United Arab Emirates': '阿联酋',
  'Saudi Arabia': '沙特阿拉伯',
  Qatar: '卡塔尔',
  Israel: '以色列',
  Turkey: '土耳其',
  Russia: '俄罗斯',
  Poland: '波兰',
  'Czech Republic': '捷克',
  Romania: '罗马尼亚',
  Bulgaria: '保加利亚',
  Croatia: '克罗地亚',
  Greece: '希腊',
  Italy: '意大利',
  Spain: '西班牙',
  Portugal: '葡萄牙',
  Netherlands: '荷兰',
  Belgium: '比利时',
  Sweden: '瑞典',
  Denmark: '丹麦',
  Finland: '芬兰',
  Norway: '挪威',
  Ireland: '爱尔兰',
  Switzerland: '瑞士',
  Austria: '奥地利',
  Kenya: '肯尼亚',
  Nigeria: '尼日利亚',
  'South Africa': '南非',
  Ghana: '加纳',
  Egypt: '埃及',
  Morocco: '摩洛哥',
  Tunisia: '突尼斯',
  Algeria: '阿尔及利亚',
  Colombia: '哥伦比亚',
  Chile: '智利',
  Peru: '秘鲁',
  Argentina: '阿根廷',
  Panama: '巴拿马',
  Guatemala: '危地马拉',
  Ecuador: '厄瓜多尔',
  Bolivia: '玻利维亚',
  Jordan: '约旦',
  Kazakhstan: '哈萨克斯坦',
  Pakistan: '巴基斯坦',
  'Sri Lanka': '斯里兰卡',
  Sudan: '苏丹',
  Iran: '伊朗',
  Latvia: '拉脱维亚',
  Moldova: '摩尔多瓦',
  Ukraine: '乌克兰',
  Belarus: '白俄罗斯',
  Unknown: '未知',
};

// ── Fraud flag names ──────────────────────────────────────────────────
const FRAUD_NAME_ZH: Record<string, string> = {
  'Free Email Provider': '免费邮箱',
  'No Web Presence': '无网络存在',
  'No Website Found': '未找到网站',
  'Dissolved Entity': '已注销实体',
  'Entity Not Found': '未找到实体',
  'Very Young Entity': '非常年轻的实体',
  'Young Entity': '年轻实体',
  'Multiple Entity Matches': '多个实体匹配',
  'Low Identity Confidence': '身份可信度低',
  'Shell Company Indicators': '空壳公司特征',
  'Recently Formed Entity': '近期成立实体',
  'Privacy Email Provider': '隐私邮箱',
  'Sanctions Risk Country': '制裁风险国家',
  'Sanctioned Country': '受制裁国家',
  'OFAC SDN Check Required': '需要OFAC审查',
  'Minimal Registered Capital': '最低注册资本',
  'Free Website Builder': '免费建站平台',
  'Country/Email Mismatch': '国家/邮箱不匹配',
  'Entity Status Mismatch': '实体状态不符',
  'Vague Business Description': '模糊的业务描述',
  'Re-export Risk': '转口风险',
};

// ── Fraud flag description patterns ───────────────────────────────────
const FRAUD_DESC_ZH: Record<string, string> = {
  'Contact uses a free email provider': '联系人使用免费邮箱',
  'instead of a corporate domain.': '而非企业域名。',
  'No company website or digital footprint detected.': '未检测到公司网站或数字足迹。',
  'Company status is Dissolved in corporate registry.': '公司在企业登记处状态为已注销。',
  'Company not found in corporate registries.': '在企业登记处未找到该公司。',
  'Company incorporated less than 2 years ago': '公司成立不到2年',
  'Company incorporated less than 5 years ago': '公司成立不到5年',
  'entities with similar names in corporate registry. Disambiguation needed.': '在企业登记处发现多个同名实体，需要消歧。',
  'Identity verification confidence is very low': '身份验证可信度非常低',
  'Contact may be misrepresenting affiliation.': '联系人可能在虚假陈述其所属关系。',
  'Company has no detectable web presence.': '未检测到公司网络存在。',
  'Company has no web presence.': '公司没有网络存在。',
  'No web presence detected.': '未检测到网络存在。',
  'Very recently formed HK company with minimal registered capital and no visible operations.': '近期成立的香港公司，注册资本极少，无可见业务运营。',
  'Company was incorporated less than 2 years ago.': '公司成立不到2年。',
  'Company formed within last 2 years.': '公司在过去2年内成立。',
  'Company formed within the last 3 years.': '公司在过去3年内成立。',
  'Uses proton.me, a privacy-focused email service uncommon for legitimate business.': '使用proton.me隐私邮箱，正规企业很少使用。',
  'Company is based in a jurisdiction with elevated sanctions risk.': '公司位于制裁风险较高的司法管辖区。',
  'Company is based in a jurisdiction with sanctions restrictions.': '公司位于受制裁限制的司法管辖区。',
  'Company is based in Iran, subject to comprehensive US/EU sanctions.': '公司位于伊朗，受全面美国/欧盟制裁。',
  'Iran-based entities require mandatory OFAC screening.': '伊朗实体需要强制OFAC审查。',
  'Registered with minimum €25K GmbH capital.': '以最低€25K GmbH资本注册。',
  'Company was dissolved/revoked in Delaware state registry.': '公司在特拉华州登记处已被注销/吊销。',
  'Representative claims active company but registry shows dissolved.': '代表声称公司活跃，但登记处显示已注销。',
  'Website hosted on Wix free tier — not a professional business site.': '网站托管在Wix免费版——非专业商业网站。',
  'No business registration found in Florida SUNBIZ.': '在佛罗里达SUNBIZ未找到商业登记。',
  'Not found in UAE DED or DMCC registry.': '在阿联酋DED或DMCC登记处未找到。',
  '"General Trading" with no specialization suggests potential front company.': '"一般贸易"无细分专业，可能是空壳公司。',
  'Not found in Irish CRO registry.': '在爱尔兰CRO登记处未找到。',
  'Not found in Jordanian Companies Control Department.': '在约旦公司监管部门未找到。',
  'Not found in French SIRENE or RCS registry.': '在法国SIRENE或RCS登记处未找到。',
  'Not found in Romanian Trade Registry.': '在罗马尼亚贸易登记处未找到。',
  'Claims Romania but uses Russian email provider.': '声称是罗马尼亚公司但使用俄罗斯邮箱。',
  'Not found in CAC registry.': '在CAC登记处未找到。',
  'Not found in MCA (Ministry of Corporate Affairs) registry.': '在MCA（企业事务部）登记处未找到。',
  "Not found in Ghana Registrar General's Department.": '在加纳总注册官部门未找到。',
  'No business registration found in any US state registry.': '在美国各州登记处均未找到商业登记。',
  'Company may re-export to sanctioned jurisdictions. Verify end-use.': '公司可能向受制裁司法管辖区转口。请核实最终用途。',
};

// ── Common industry names ─────────────────────────────────────────────
const INDUSTRY_ZH: Record<string, string> = {
  'Consumer Electronics': '消费电子',
  'Consumer Electronics Distribution': '消费电子分销',
  'Consumer Electronics Import': '消费电子进口',
  'Consumer Electronics Retail': '消费电子零售',
  'Consumer Electronics E-commerce': '消费电子电商',
  'Electronics Distribution': '电子产品分销',
  'Electronics Wholesale': '电子产品批发',
  'Electronics Retail': '电子产品零售',
  'Electronics Import': '电子产品进口',
  'Electronics Trading': '电子产品贸易',
  'Electronic Components': '电子元器件',
  'Electronic Components Trading': '电子元器件贸易',
  'Electronic Components Retail': '电子元器件零售',
  'Electronic Manufacturing': '电子制造',
  'Electronics Manufacturing': '电子制造',
  'Electronics Re-export': '电子产品转口',
  'Electronics Free Zone Trading': '电子产品自贸区贸易',
  'E-commerce Electronics': '电子商务电子产品',
  'Consumer Tech Accessories': '消费科技配件',
  'Computer & Electronics Retail': '电脑及电子产品零售',
  'IoT & Smart Devices': '物联网与智能设备',
  'Smart Home Devices': '智能家居设备',
  'Consumer Electronics Import/Export': '消费电子进出口',
  'General Trading': '一般贸易',
  'Import/Export': '进出口',
  Trading: '贸易',
  Wholesale: '批发',
  Unknown: '未知',
};

// ── Public API ────────────────────────────────────────────────────────

/** Translate a single value through a map; passthrough if no match */
function tc(value: string | null | undefined, map: Record<string, string>): string {
  if (!value) return '';
  return map[value] ?? value;
}

/** Translate an array of values */
function tcArray(arr: string[] | null | undefined, map: Record<string, string>): string[] {
  if (!arr) return [];
  return arr.map((v) => map[v] ?? v);
}

/** Translate a fraud flag */
function tcFraudFlag(flag: { name: string; description: string }) {
  const name = FRAUD_NAME_ZH[flag.name] ?? flag.name;
  // Try exact match first, then partial replacements
  let desc = FRAUD_DESC_ZH[flag.description];
  if (!desc) {
    desc = flag.description;
    // Apply partial translations for templated descriptions
    for (const [en, zh] of Object.entries(FRAUD_DESC_ZH)) {
      if (desc.includes(en)) {
        desc = desc.replace(en, zh);
      }
    }
  }
  return { name, description: desc };
}

/** Identity passthrough — returns value unchanged */
function pass(value: string | null | undefined): string {
  return value ?? '';
}
function passArray(arr: string[] | null | undefined): string[] {
  return arr ?? [];
}
function passFraudFlag(flag: { name: string; description: string }) {
  return flag;
}

/**
 * Returns content translation functions based on the active language.
 * When lang is 'en', all functions are identity/passthrough.
 * When lang is 'zh', values are mapped through Chinese dictionaries.
 */
export function useTranslateContent(lang: Lang) {
  if (lang === 'en') {
    return {
      status: pass,
      riskTier: pass,
      payment: pass,
      creditRating: pass,
      relevance: pass,
      market: pass,
      markets: passArray,
      country: pass,
      industry: pass,
      industryTags: passArray,
      fraudFlag: passFraudFlag,
    };
  }

  return {
    status: (v: string | null | undefined) => tc(v?.toLowerCase(), STATUS_ZH),
    riskTier: (v: string | null | undefined) => tc(v?.toLowerCase(), RISK_TIER_ZH),
    payment: (v: string | null | undefined) => tc(v, PAYMENT_ZH),
    creditRating: (v: string | null | undefined) => tc(v, CREDIT_RATING_ZH),
    relevance: (v: string | null | undefined) => tc(v?.toLowerCase(), RELEVANCE_ZH),
    market: (v: string | null | undefined) => tc(v, MARKET_ZH),
    markets: (arr: string[] | null | undefined) => tcArray(arr, MARKET_ZH),
    country: (v: string | null | undefined) => tc(v, COUNTRY_ZH),
    industry: (v: string | null | undefined) => tc(v, INDUSTRY_ZH),
    industryTags: (arr: string[] | null | undefined) => tcArray(arr, INDUSTRY_ZH),
    fraudFlag: tcFraudFlag,
  };
}

export { RISK_TIER_ZH };
